import type { DateOnly } from '@atlassian/jira-platform-utils-date-only/src/index.tsx';
import type { UseCalendarItemsRequest } from './types.tsx';

type toJqlArgs = {
	jqlContext: string;
	dueDateFieldId: string;
	startOfMonthDate: Date | DateOnly;
	endOfMonthDate: Date | DateOnly;
	quickFiltersJql?: null | string;
	startDateFieldId?: null | string;
};

/**
 * Returns a JQL string that represents a search for a specified project, between
 * and inclusively between the start and end date, for the supplied field, taking into account any filters that are currently set.
 */
export const toJQL = ({
	jqlContext,
	dueDateFieldId,
	startOfMonthDate,
	endOfMonthDate,
	quickFiltersJql,
	startDateFieldId,
}: toJqlArgs): string => {
	const startOfMonthDateISOString = startOfMonthDate.toISOString();
	const endOfMonthDateISOString = endOfMonthDate.toISOString();

	const inThisMonthQuery = (fieldName: string) =>
		`(${fieldName} != NULL AND ${fieldName}>=${startOfMonthDateISOString} AND ${fieldName}<=${endOfMonthDateISOString})`;

	const isDueDateInMonthQuery = inThisMonthQuery(dueDateFieldId);

	let defaultClauseWithSubTasks;

	if (startDateFieldId != null) {
		const startDateFieldQuery = `cf[${startDateFieldId}]`;

		const isStartDateInMonthQuery = inThisMonthQuery(startDateFieldQuery);
		const isStartDateAndDueDateWithinMonthQuery = `(${dueDateFieldId} != NULL AND ${startDateFieldQuery} != NULL AND ${dueDateFieldId}>=${startOfMonthDateISOString} AND ${startDateFieldQuery}<=${endOfMonthDateISOString})`;

		/**
		 * start date != null && in this month ||
		 * due date != null and in this month ||
		 * both != null and in this month in range
		 */
		defaultClauseWithSubTasks = [
			isDueDateInMonthQuery,
			isStartDateInMonthQuery,
			isStartDateAndDueDateWithinMonthQuery,
		].join(' OR ');
	} else {
		defaultClauseWithSubTasks = isDueDateInMonthQuery;
	}

	const jql = [jqlContext, `(${defaultClauseWithSubTasks})`];

	if (quickFiltersJql != null) {
		jql.push(quickFiltersJql);
	}

	return `${jql.join(' AND ')} ORDER BY "${dueDateFieldId}" ASC`;
};

export const toQuery = (
	{ jqlContext, startDate, endDate }: UseCalendarItemsRequest,
	quickFiltersJql?: null | string,
	startDateFieldId?: null | string,
): string =>
	toJQL({
		jqlContext,
		dueDateFieldId: 'duedate',
		startOfMonthDate: startDate,
		endOfMonthDate: endDate,
		quickFiltersJql,
		startDateFieldId,
	});
